import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ConflictAlert,
  RentalItemPicker,
} from '@/modules/reservations/components';
import { useReservationAvailability } from '@/modules/reservations/hooks';
import { env } from '@/config/env';
import {
  getReservation,
  listRentableSelectableItems,
  saveReservation,
} from '@/modules/reservations/services';
import {
  createReservationViaWorker,
  formatReservationApiError,
  isWorkerReservationsEnabled,
} from '@/services/api/reservations.api';
import {
  RESERVATION_STATUS_LABELS,
  type ReservationInput,
  type ReservationStatus,
} from '@/modules/reservations/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import type { InventoryItem } from '@/modules/inventory/types';

const defaultInput: ReservationInput = {
  title: '',
  description: '',
  inventoryItemIds: [],
  clientName: '',
  startDate: '',
  endDate: '',
  status: 'draft',
  notes: '',
};

const WRITABLE_STATUSES: ReservationStatus[] = [
  'draft',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
];

export function ReservationFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('reservations.write');
  const { checking, result, error: availError, check, reset } =
    useReservationAvailability();

  const [form, setForm] = useState<ReservationInput>(defaultInput);
  const [rentableItems, setRentableItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validated, setValidated] = useState(false);
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string | undefined>();

  useEffect(() => {
    void listRentableSelectableItems().then(setRentableItems);
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getReservation(id)
      .then((reservation) => {
        if (!reservation) {
          setError('Reserva no encontrada.');
          return;
        }
        setForm({
          title: reservation.title,
          description: reservation.description,
          inventoryItemIds: reservation.inventoryItemIds,
          clientName: reservation.clientName,
          startDate: reservation.startDate,
          endDate: reservation.endDate,
          status: reservation.status,
          notes: reservation.notes,
        });
        setLoadedUpdatedAt(reservation.updatedAt.toISOString());
      })
      .catch(() => setError('Error al cargar la reserva.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    setValidated(false);
    reset();
  }, [
    form.startDate,
    form.endDate,
    form.inventoryItemIds,
    form.status,
    reset,
  ]);

  async function handleValidate() {
    setError(null);
    if (!form.title.trim()) {
      setError('El título es obligatorio.');
      return;
    }
    if (form.inventoryItemIds.length === 0) {
      setError('Seleccioná al menos un artículo alquilable.');
      return;
    }
    const availability = await check(
      isNew || !id
        ? {
            inventoryItemIds: form.inventoryItemIds,
            startDate: form.startDate,
            endDate: form.endDate,
          }
        : {
            inventoryItemIds: form.inventoryItemIds,
            startDate: form.startDate,
            endDate: form.endDate,
            excludeReservationId: id,
          },
    );
    setValidated(availability?.available === true);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !profile) return;

    if (form.status === 'confirmed' || form.status === 'in_progress') {
      if (!validated) {
        setError(
          'Verificá la disponibilidad antes de guardar una reserva confirmada o en curso.',
        );
        return;
      }
      const availability = await check(
        isNew || !id
          ? {
              inventoryItemIds: form.inventoryItemIds,
              startDate: form.startDate,
              endDate: form.endDate,
            }
          : {
              inventoryItemIds: form.inventoryItemIds,
              startDate: form.startDate,
              endDate: form.endDate,
              excludeReservationId: id,
            },
      );
      if (!availability?.available) return;
    }

    setSaving(true);
    setError(null);
    try {
      const useWorker =
        env.useWorkerReservations && isWorkerReservationsEnabled();
      const saved =
        isNew && useWorker
          ? await createReservationViaWorker(form)
          : await saveReservation(
              form,
              profile.uid,
              isNew ? undefined : id,
              loadedUpdatedAt,
            );
      void navigate(`${modulePaths.reservations}/${saved.id}`, {
        replace: true,
      });
    } catch (err) {
      setError(formatReservationApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  return (
    <section>
      <p>
        <Link to={modulePaths.reservations}>← Volver al listado</Link>
      </p>
      <h1>{isNew ? 'Nueva reserva' : 'Editar reserva'}</h1>

      <form className="inventory-panel" onSubmit={handleSubmit}>
        <div className="users-form-grid">
          <label className="auth-label">
            Título *
            <input
              required
              disabled={!canWrite}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Cliente
            <input
              disabled={!canWrite}
              value={form.clientName}
              onChange={(e) => setForm({ ...form, clientName: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Desde *
            <input
              type="date"
              required
              disabled={!canWrite}
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Hasta *
            <input
              type="date"
              required
              disabled={!canWrite}
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Estado
            <select
              disabled={!canWrite}
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as ReservationStatus,
                })
              }
            >
              {WRITABLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {RESERVATION_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="auth-label">
          Descripción
          <textarea
            rows={2}
            disabled={!canWrite}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>

        <label className="auth-label">
          Notas
          <textarea
            rows={2}
            disabled={!canWrite}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        <fieldset className="inventory-fieldset">
          <legend>Artículos alquilables</legend>
          <RentalItemPicker
            items={rentableItems}
            selectedIds={form.inventoryItemIds}
            disabled={!canWrite}
            onChange={(ids) => setForm({ ...form, inventoryItemIds: ids })}
          />
        </fieldset>

        {canWrite ? (
          <div className="inventory-actions">
            <button
              type="button"
              className="auth-button auth-button--secondary"
              disabled={checking}
              onClick={() => void handleValidate()}
            >
              {checking ? 'Verificando…' : 'Verificar disponibilidad'}
            </button>
            {validated ? (
              <span className="reservation-validated">Disponibilidad OK</span>
            ) : null}
          </div>
        ) : null}

        <ConflictAlert
          conflicts={result?.conflicts ?? []}
          {...(result?.ineligibleItems && result.ineligibleItems.length > 0
            ? { ineligibleItems: result.ineligibleItems }
            : {})}
          dateRangeInvalid={result ? !result.dateRangeValid : false}
        />
        {availError ? <p className="auth-error">{availError}</p> : null}
        {error ? <p className="auth-error">{error}</p> : null}

        {canWrite ? (
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        ) : null}
      </form>
    </section>
  );
}
